<template>
  <div class="user-guide-page">
    <div class="button-row">
      <button class="back-button" @click="$router.go(-1)" aria-label="Voltar">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div class="tab-buttons">
        <button
          :class="['tab-button', currentTab === 'padrão' ? 'active' : '']"
          @click="goToTab('padrão')"
        >
          Modo geral
        </button>
        <button
          v-if="hasWikiPermission"
          :class="['tab-button', currentTab === 'wiki' ? 'active' : '']"
          @click="goToTab('wiki')"
        >
          Modo Wiki
        </button>
        <button
          :class="['tab-button', currentTab === 'projetos' ? 'active' : '']"
          @click="goToTab('projetos')"
        >
          Projetos
        </button>
        <button
          :class="['tab-button', currentTab === 'datalake' ? 'active' : '']"
          @click="goToTab('datalake')"
        >
          Datalake
        </button>
        <button
          :class="['tab-button', currentTab === 'relatorios' ? 'active' : '']"
          @click="goToTab('relatorios')"
        >
          Qualificação CNPJ
        </button>

      </div>
    </div>

    <h1 v-if="currentTab === 'padrão'">Guia de Uso – SilvIA (Modo Geral)</h1>
    <h1 v-else-if="currentTab === 'wiki'">Guia de Uso – Modo Wiki da SilvIA</h1>
    <h1 v-else-if="currentTab === 'projetos'">Guia de Uso – Aba de Projetos da SilvIA</h1>
    <h1 v-else-if="currentTab === 'datalake'">Guia de Uso – Modo Datalake da SilvIA</h1>
    <h1 v-else>Guia de Uso – Relatórios da SilvIA</h1>


    <div class="content" v-html="renderedGuide"></div>
  </div>
</template>

<script>
import { marked } from "marked";
import DOMPurify from "dompurify";

export default {
  name: "UserGuide",
  data() {
    const queryTab = this.$route.query.tab;
    const defaultTab = ["padrão", "wiki", "projetos"].includes(queryTab)
      ? queryTab
      : "padrão";

    return {
      currentTab: defaultTab,
      guides: {
        padrão: `

SilvIA é a assistente virtual da ViaInvest, projetada para **responder perguntas**, **esclarecer dúvidas** e **auxiliar no dia a dia** dos colaboradores.
No seu modo padrão, ela atua como uma ferramenta de consulta inteligente, dispondo de diversas fontes de conhecimento, como o nosso **wiki interno**, seu conhecimento próprio como inteligência artificial, e buscas na web. 

Este guia foi criado para explicar como funciona essa busca de informações, e como você pode utilizar e tirar o máximo proveito da SilvIA.

---

## O que a SilvIA faz no modo padrão?

No modo padrão, a SilvIA é capaz de:

- Responder perguntas dos usuários;
- Buscar informações diretamente nas páginas do Wiki;
- Complementar as respostas com seu conhecimento integrado, quando necessário;
- Buscar informações na web;
- Gerar arquivos novos com base nas solicitações do usuário (TXT, DOCX, PDF, XLSX, entre outros);
- Interpretar, ler e analisar arquivos enviados pelos usuários (documentos de texto, planilhas, PDFs, entre outros).

Ela é uma ferramenta de apoio que pode esclarecer desde **regras internas**, **políticas da empresa**, até **procedimentos técnicos** - tudo com agilidade e precisão.

---

## Como é a divisão dos modos de pergunta?

As perguntas enviadas para a SilvIA são divididas em 3 modos, ativados pelos botões na interface:

- Padrão: Nenhum botão ativado, SilvIA responde de acordo com seus conhecimentos próprios de treinamento;
- Wiki: Com o botão Wiki ativado, a SilvIA realiza uma busca no wiki interno para encontrar a resposta desejada pelo usuário;
- Busca Web: Com o botão de busca ativado, a SilvIA pesquisa na internet as fontes mais atualizadas de informação para trazer a resposta ao usuário.

Todos os modos contam com análise e geração de arquivos ativas nativamente.

---

## 1. Buscas no wiki

### 1.1 - Como funciona a busca no wiki

Sempre que você envia uma pergunta com o botão "wiki" pressionado, a SilvIA realiza uma busca no Wiki para encontrar a resposta mais adequada. Esse processo funciona assim:

1. Ela analisa os termos principais da sua pergunta;
2. Realiza uma busca com base:
   - No título das páginas;
   - E nas palavras-chave presentes no início do conteúdo das páginas;
3. Caso encontre uma correspondência relevante, responde com base nela;
4. Se não encontrar nada compatível, utiliza seu conhecimento próprio para tentar responder da melhor forma possível.

### 1.2 - Seja específico ao perguntar

A qualidade da resposta da IA depende diretamente da clareza e objetividade da sua pergunta.

- Sempre que possível, informe o nome exato da página que deseja consultar;
- Ou utilize palavras-chave que provavelmente estão no título ou no começo da página.

**Exemplo de pergunta genérica (resultado impreciso):**  
> "Como funciona o processo?"

**Exemplo realista de consulta ideal:**  
> "Quem estará na escala de plantão da Soulpay para março de 2025?"

Fazemos essa consulta citando o nome da página, que é **ESCALA - PLANTÃO SOULPAY**. Não precisamos escrever o nome exatamente, mas palavras importantes dele
para que a IA consiga interpretar quais páginas são mais relevantes para sua busca.

### 1.3 - Quando a resposta não vem do Wiki

Se a SilvIA não encontrar uma página adequada para responder sua pergunta, ela irá recorrer ao seu conhecimento interno para formular uma resposta baseada em boas práticas, contexto institucional e informações gerais.

- Nesse caso, ela pode informar que não encontrou uma fonte específica, e ainda assim tentará ajudar com base no que sabe;
- Se for um tema sensível, específico ou regulado por política interna, **é sempre indicado confirmar a informação com um responsável da área ou consultando diretamente o Wiki**. Se a SilvIA não encontrou a resposta, não significa que ela não existe no wiki. Sempre é válido procurar manualmente neste caso.

---

## 2. Busca na Web

### 2.1 - Como funcionam as buscas na web

Quando o botão de busca na Web é pressionado para enviar uma pergunta, a SilvIA sempre irá usar sua ferramenta de pesquisa na internet para encontrar o que o usuário perguntou. Ela irá realizar uma busca, ler diversas páginas e comparar resultados para responder o usuário, e sempre irá trazer as fontes de sua pesquisa.
Este modo é recomendado para encontrar informações sobre dados que devem estar atualizados, como cotações de ações, valor do dólar, novas leis, notícias, etc.

---

## 3. Uso de arquivos

Além de responder perguntas, buscar na internet e no wiki, a SilvIA possui funcionalidades de **criação e análise de arquivos**:

### Criação de arquivos
- A SilvIA pode **gerar arquivos novos** em diferentes formatos (TXT, DOCX, PDF, XLSX, etc.) com base nas suas solicitações;
- Esses arquivos podem conter relatórios, resumos, planilhas ou qualquer informação solicitada.

### Interpretação e leitura de arquivos enviados
- Você pode enviar arquivos para que a SilvIA **abra, leia e analise** o conteúdo;
- É possível pedir para ela responder perguntas diretamente sobre esses arquivos;
- Arquivos suportados incluem: **documentos de texto, planilhas, PDFs, imagens e outros formatos comuns**.

 **Exemplo de uso prático:**  
- "SilvIA, analise essa planilha e me diga qual foi o total de vendas em abril."  
- "SilvIA, gere um PDF com as regras de conduta e boas práticas." 

---

## 4. Limites de conhecimento da IA

Por limitações da própria tecologia, a SilvIA no modo padrão possui um **corte de conhecimento geral em 31 de maio de 2024**.
Isso significa que:

- Ela não tem acesso a fatos, dados públicos ou atualizações que tenham ocorrido depois dessa data (por exemplo, taxas atuais, mudanças recentes de leis e regras, etc), a menos que estejam no Wiki ou que seja encontrado no modo Web;
- Para conseguir dados atualizados, é necessário usar o modo de busca na Web.

---

## 5. Recomendações para um uso eficiente

- **7.1** – Saiba o que você está procurando antes de perguntar. Isso ajuda a formular uma boa solicitação;
- **7.2** – Inclua nomes de páginas, termos específicos e contexto no seu pedido;
- **7.3** – **Evite perguntas vagas ou muito genéricas**. A IA pode tentar responder, mas o resultado pode não ser o esperado;
- **7.4** – Se a resposta parecer incompleta ou imprecisa, refaça a pergunta com mais detalhes ou peça esclarecimentos;
- **7.5** – A SilvIA é uma inteligência artificial e pode errar. **Revisar e confirmar informações sensíveis é sempre uma boa prática**.
- **7.6** - Os arquivos gerados pela SilvIA, assim como as páginas geradas do wiki vão ser criados exatamente da forma que você pedir. Logo, quanto menos informações ela tiver sobre o que você pedir, menos conteúdo terá o arquivo gerado. Sempre faça solicitações ricas em informações e contexto para receber o arquivo completo que deseja.

---

## 8. Avisos importantes sobre o uso da SilvIA

- **8.1** – Todas as interações com a IA são **monitoradas e registradas**, visando segurança, controle de qualidade e melhoria da ferramenta;  
- **8.2** – Se você encontrar erros, problemas ou respostas inesperadas, **entre em contato com os desenvolvedores da SilvIA**;  
- **8.3** – Sugestões, ideias e feedbacks são bem-vindos, e ajudam a tornar a IA cada vez mais útil;  
- **8.4** – O uso inadequado ou intencionalmente incorreto da IA pode acarretar em **advertência ou perda de acesso** à ferramenta.

---

## Considerações finais

SilvIA está aqui para facilitar o seu trabalho, esclarecer dúvidas com agilidade e aumentar sua produtividade.  
Quanto mais clara for sua pergunta, mais precisa será a resposta.`,
        wiki: `**SilvIA** é a assistente virtual da ViaInvest, e no modo Wiki, ela assume o papel de aliada na criação e edição de páginas no nosso Wiki corporativo.  
Este guia foi elaborado para ajudar você a utilizar esse modo de forma eficiente, mantendo a qualidade e a organização do nosso conteúdo interno.

---

## O que é o Modo Wiki da SilvIA?

Com o modo Wiki ativado, a SilvIA é capaz de:

- **Editar** páginas existentes, conforme as instruções recebidas;
- **Criar** novas páginas, com base no que você solicitar.

O uso dessa funcionalidade exige **atenção e cuidado**, já que a IA executará as ações exatamente como forem solicitadas.

---

## 1. A SilvIA executa exatamente o que for pedido, do jeito que for pedido

A SilvIA não interpreta intenções vagas. Ela realiza fielmente o que você escrever.  
Por isso, preste muita atenção nas instruções que for enviar, e certifique-se de que estão **bem detalhadas e específicas**.

**Exemplo de instrução genérica (pouco eficaz):**  
> "Crie uma página para falar sobre os melhores fundos de investimento."

Neste caso, a SilvIA:
- Vai escolher sozinha a categoria onde a página estará;
- Vai definir sozinha o conteúdo (baseando-se no conhecimento que possui);
- Pode criar algo diferente do que você esperava.

Sempre valorize mensagens detalhadas, explícitas e compatíveis com o que realmente deseja.  
Esse cuidado é essencial para mantermos a **organização e a qualidade do nosso Wiki**.

---

## 2. Informe onde a página deve ser criada

Embora não seja um campo obrigatório, é importante escolher a categoria onde estará a página a ser criada. Sem ela, as páginas soltas podem se perder pelo Wiki, e se tornar difíceis de encontrar.

- Sempre que possível, informe a categoria onde a nova página deve ser inserida.

**Exemplo de instrução completa:**  
>- Usuário seleciona uma categoria desejada no menu da SilvIA, como Tecnologia da Informação;
>- Usuário digita o nome da página no campo designado (obrigatório), como Documentação do Projeto SilvIA;
>- Usuário digita seu prompt: "SilvIA, crie a página Documentação do Projeto SilvIA, citando nela os benefícios do uso da SilvIA para produtividade dentro da ViaInvest, citando os pontos X, Y e Z do processo de desenvolvimento, e as seguintes instruções para uso da ferramenta: (...)";
>- Usuário seleciona a opção de criação e envia.

---

## 3. Quanto mais detalhado for o pedido, melhor será o resultado

Essa dica vale **para qualquer uso de inteligência artificial** – e especialmente para o modo Wiki da SilvIA.

- Sempre que for fazer uma solicitação, pense bem nas informações e contexto que está passando.
- Quanto mais ela souber sobre o que você quer, melhor será o resultado entregue.

**Exemplo de prompt ruim (ineficiente):**  
> “Edite a conciliação de arquivos e melhore.”

**Exemplo de prompt bom:**  
> “Edite a página *Teste da SilvIA*. Retire o conteúdo genérico de testes e coloque um ranking com as 10 melhores práticas para criação de prompts para IA. Certifique-se de que o conteúdo esteja bem formatado.”

O segredo está em escrever uma solicitação clara e bem estruturada, que economiza tempo e evita retrabalho.

---

## 4. Dicas gerais para edição de páginas

- **4.1** – Quanto mais explícito e bem explicado, melhor;  
- **4.2** – Diga o que quer alterar e o que deseja manter. Isso evita modificações não intencionais.

**Exemplo:**  
> “SilvIA, edite a página *Teste da SilvIA*. Em vez de 10 melhores práticas, agora deixe 15. Não altere as que já estavam na página, apenas acrescente mais 5. Não altere mais nada.”

---

## 5. Dicas gerais para criação de páginas

- **5.1** – Clareza sempre é essencial;  
- **5.2** – Informe **onde** a página deve estar (categoria já existente, com nome exato, ou nova categoria);  
- **5.3** – Explique claramente o que você espera do conteúdo da nova página.

**Exemplo de prompt eficiente:**  
> “Crie uma página chamada *Convenções de Escrita de E-mail*. A página deve explicar os requisitos de um e-mail profissional na ViaInvest: ortografia correta, assinatura com nome e setor, assunto coerente, e ao final do e-mail incluir ‘A Via é o caminho’. Escreva isso de forma profissional, detalhada e em formato de passo a passo.”

---

## 6. Recomendações gerais de uso do modo Wiki

- **6.1** – Sempre revise suas instruções antes de enviá-las;  
- **6.2** – A IA facilita, mas é importante explicar bem o que é desejado, para conseguir instruí-la corretamente;  
- **6.3** – A verificação do conteúdo gerado é indispensável.  
  A IA pode errar – e é responsabilidade do usuário **revisar tudo com atenção**.  
  Após cada criação ou edição, acesse a página modificada, leia o conteúdo, e ajuste o que for necessário.

---

## 7. Informações e avisos importantes sobre o uso da SilvIA

- **7.1** – Todas as mensagens (do usuário e da IA) são **armazenadas e monitoradas**, para fins de segurança, controle de qualidade e melhoria contínua da ferramenta;  
- **7.2** – Caso você perceba erros, comportamentos inesperados ou problemas na interface, avise os desenvolvedores da SilvIA;  
- **7.3** – **Sugestões, ideias e melhorias são sempre bem-vindas**, e ajudam a tornar a SilvIA uma ferramenta cada vez mais útil para todos;  
- **7.4** – O uso incorreto ou intencionalmente inadequado da IA pode resultar em advertência e até na suspensão de acesso.

---

## Considerações finais

A SilvIA foi desenvolvida para **aumentar a produtividade** e **facilitar a organização do conhecimento dentro da empresa**.  
Seu uso consciente, bem orientado e estruturado é fundamental para garantir resultados positivos e manter a excelência do nosso Wiki.`,
        projetos: `

A funcionalidade **Projetos** da SilvIA foi criada para organizar conversas e adaptar o comportamento da assistente para contextos específicos — de forma parecida com os “projetos” do ChatGPT.

Use projetos quando precisar de foco, consistência e regras claras que se apliquem a várias conversas relacionadas (como uma pasta com comportamento próprio).

---

##  O que os Projetos fazem

Com Projetos você pode:

- **Organizar conversas como pastas:** agrupe chats relacionados para fácil acesso e histórico.  
- **Criar ambientes/contextos especializados:** cada projeto pode ter um “tom”, regras e prioridades diferentes.  
- **Definir instruções personalizadas (mensagem de sistema):** faça a SilvIA agir como especialista/assistente com regras fixas.  
- **Enviar arquivos de projeto:** planilhas, documentos, PDFs ou bases exportadas usados como fonte confiável.  
- **Compartilhar projetos com outros usuários:** colaboração em cima de um mesmo contexto e arquivos, com permissões específicas.  

---

##  Como os Projetos funcionam

Um **Projeto = pasta + instrução de sistema + arquivos opcionais + permissões de compartilhamento**.

### Prioridade das instruções
- As instruções do projeto são enviadas como mensagem de sistema e têm prioridade sobre o comportamento padrão.  
- Instruções passadas dentro de uma conversa podem complementar, mas não devem contrariar regras críticas definidas no projeto.  
- Arquivos servem como fonte de conhecimento adicional, mas não substituem instruções.  

### Herança e escopo
- Toda conversa dentro do projeto recebe automaticamente a instrução do projeto.  
- Arquivos ficam disponíveis como base em todas as conversas do projeto.  
- Conversas fora do projeto seguem o modo padrão da SilvIA e não têm acesso a esses arquivos.  
- Em projetos compartilhados, cada usuário tem acesso apenas ao **seu próprio histórico de conversas**, mantendo a privacidade.  

---

##  Permissões nos projetos

- **Somente leitura** → Uso de arquivos e instrução personalizada em conversas próprias;  
- **Editor** → Uso de arquivos e instrução personalizada em conversas próprias, edição de instrução personalizada e arquivos de projeto;
- **Administrador** → Uso de arquivos e instrução personalizada em conversas próprias, edição de instrução personalizada e arquivos de projeto e gerenciamento de usuários;
- **Proprietário** → Quem criou o projeto; Pode realizar todas as ações anteriores, e renomear ou deletar o projeto.  

---

##  Exemplo prático: RH em um processo seletivo

Um recrutador cria o projeto **“Processo Seletivo – Analista de Desenvolvimento Jr”** e define na instrução personalizada:  

> “Você é um sistema de triagem avançado que analisa currículos e avalia a correspondência do candidato com a vaga descrita, de maneira detalhada.”  

Ele adiciona dois arquivos:  
- **Descrição da vaga.docx** (requisitos e responsabilidades).  
- **Lista de candidatos.xlsx** (informações gerais dos inscritos).  

O projeto é então **compartilhado com a equipe**:  
- Editores podem atualizar arquivos e instruções.  
- Administradores podem gerenciar usuários.  
- Cada recrutador cria suas próprias conversas, mas só enxerga o próprio histórico.  

**Exemplo de uso em conversa:** um recrutador envia o PDF de um currículo e pede para a SilvIA avaliar. A IA cruza os dados do candidato com a descrição da vaga, destaca pontos fortes/fracos e gera uma **pontuação de aderência**.  

Dessa forma, a equipe inteira trabalha com o mesmo contexto e mantém consistência, mas sem perder a individualidade do histórico de cada membro.  

---

##  Criar e gerenciar projetos — passos práticos

### Criar projeto
- Nomeie de forma clara e curta (ex.: *Finanças Pessoais*, *Suporte Técnico*, *Onboarding RH*).  
- Escreva a instrução personalizada (mensagem de sistema).  
- (Opcional) Faça upload de arquivos de projeto.  

### Editar projeto
- Alterar nome;  
- Atualizar instrução;  
- Adicionar ou remover arquivos (arquivos deletados não poderão mais ser usados).  

### Adicionar conversas
- Mova conversas existentes para o projeto ou crie novas já dentro dele.  

### Excluir projeto
- Ao excluir, todas as conversas dentro dele também serão deletadas.  

---

##  Boas práticas para arquivos de projeto

- Prefira formatos abertos (CSV, XLSX, TXT, PDF).  
- Mantenha arquivos atualizados para não comprometer a análise.  
- Documente os arquivos e seu uso (ex.: *“Descrição da vaga”*, *“Lista de candidatos”*).  
- Evite informações sensíveis sem regras claras de segurança.  

---

 **Resumo:** Os Projetos da SilvIA organizam, padronizam e permitem colaboração estruturada. Eles fornecem consistência para equipes sem abrir mão da privacidade de cada colaborador.`,
 datalake:  ` 

O modo **DataLake** da SilvIA é a primeira integração direta da nossa inteligência artificial com o OpenSearch.  
Ele marca um passo importante rumo ao uso real de dados em nossas operações, permitindo consultas ágeis, respostas contextualizadas e análises geradas automaticamente com base nas informações disponíveis em nosso banco de dados.

---

## O que o modo DataLake faz?

Ao ativar o botão **“DataLake”**, a SilvIA passa a:

- **Consultar dados no DataLake**: Ela interpreta a pergunta do usuário, identifica parâmetros relevantes (como EC, datas, status, valores etc.) e executa uma consulta pré-definida no DataLake.
- **Gerar insights personalizados**: Com o resultado da consulta, a SilvIA analisa o contexto da pergunta e produz uma resposta explicativa e detalhada.
- **Responder perguntas adicionais**: Após a resposta inicial, o usuário pode pedir esclarecimentos ou análises complementares, e a SilvIA utilizará tanto os dados já obtidos quanto o contexto da conversa.

> **Importante:**  
> Além das informações retornadas pela consulta, a SilvIA é capaz de **realizar cálculos internos** nos seus insights — como variações, médias, proporções, diferenças e comparativos — mesmo que esses valores não venham diretamente agregados do banco de dados.  
> Esses cálculos são feitos pela própria IA com base nos dados brutos recebidos.

---

## Como o modo DataLake funciona?

O fluxo do modo DataLake segue estes passos:

1. O usuário ativa o modo e envia sua pergunta.  
2. A SilvIA extrai parâmetros relevantes da frase (ex.: EC informado, intervalo de datas, valor mínimo, bandeira etc.).  
3. A consulta correspondente é escolhida e executada no banco de dados.  
4. A SilvIA recebe o resultado, analisa a pergunta novamente e monta uma resposta coerente, útil e contextualizada.

### Informações importantes sobre as consultas:

- O conjunto de consultas disponíveis é **limitado e pré-configurado**.  
  No momento, SilvIA acessa apenas dados da Soulpay referentes a **transações e parcelas**.
- As buscas podem usar filtros como ECs, datas, CNPJ, NSU, valores, bandeiras, status de parcela e tipos de pagamento.
- A lista de consultas disponíveis será expandida gradualmente com novas bases e novos formatos.
- Por segurança e controle, **a IA não pode gerar consultas por conta própria**. cada consulta é criada manualmente pela equipe, cobrindo cenários necessários e comuns.
- Se você identificar falta de algum tipo de busca, resultados inconsistentes ou limitações, entre em contato com o time de desenvolvimento para melhorias.

---

## Exemplos de uso

### Consulta de maiores transações em um mês

O usuário ativa o DataLake e envia:

> **"SilvIA, por favor traga pra mim as maiores transações feitas em maio de 2024."**

A SilvIA poderia responder algo como:

> #### 🏆 Top 5 Maiores Transações — Maio/2024
>
> Olá, Renato! Aqui vão as maiores transações registradas em maio de 2024:
>
> | Data       | Estabelecimento     | Valor Bruto (R$) | Valor Líquido (R$) | Status     | Bandeira   | Plataforma  |
> |-----------|----------------------|------------------|---------------------|------------|------------|-------------|
> | 03/05/2024 | C BLINDADOS S.A.     | 125.700,00       | 121.199,77          | Recusado   | Amex       | E-Commerce  |
> | 27/05/2024 | C BLINDADOS S.A.     | 99.100,00        | 96.136,92           | Antecipado | Mastercard | E-Commerce  |
> | 29/05/2024 | C BLINDADOS S.A.     | 94.500,00        | 91.674,46           | Antecipado | Mastercard | E-Commerce  |
> | 07/05/2024 | C BLINDADOS S.A.     | 85.280,00        | 82.730,14           | Recusado   | Visa       | E-Commerce  |
> | 07/05/2024 | C BLINDADOS S.A.     | 85.280,00        | 82.730,14           | Antecipado | Visa       | E-Commerce  |
>
> **Insights rápidos:**
> - Valores brutos variam de **R$ 40.000,00** a **R$ 125.700,00**  
> - Maior valor líquido: **R$ 121.199,77**  
> - Quase todas as maiores transações pertencem ao mesmo estabelecimento  
> - Destaque para Amex, Mastercard e Visa  
> - Predomínio de operações via **E-Commerce**
>
> **Curiosidades:**
> - Algumas operações muito altas foram **recusadas**  
> - Pode indicar tentativas fora do perfil esperado do EC  
>
> “Se quiser mais detalhes ou comparativos, é só pedir!"

---

## Boas práticas de uso do modo DataLake

- Quanto mais detalhes você der, mais preciso será o retorno.  
  Ex.:  
  > “SilvIA, quantas transações foram feitas para o EC 3004 em 2024?”
- Especifique parâmetros sempre que possível: EC, datas, status, plataforma, bandeira etc.
- Prefira perguntas claras e objetivas — isso ajuda a IA a selecionar a consulta correta e montar insights mais úteis.

---

## Considerações finais

O modo DataLake inaugura o uso real de dados na SilvIA, facilitando o acesso à informação e fortalecendo a tomada de decisões dentro da Soulpay.  
Essa é apenas a primeira versão da integração — novas consultas e funcionalidades serão adicionadas com o tempo.

Caso encontre limitações, erros ou note ausência de alguma consulta relevante, entre em contato com o time de TI para que possamos continuar aprimorando a ferramenta.
`, 
relatorios: `# Guia de Uso - SilvIA (Modo de Qualificação de CNPJ)

A funcionalidade de Qualificação de CNPJ da SilvIA foi desenvolvida para a equipe comercial, com o objetivo de agilizar e facilitar a consulta de informações de empresas contatadas em eventos ou avaliadas como possíveis novos clientes da Via.  
Com a Qualificação de CNPJ, a SilvIA passa a atuar como uma especialista em análise de empresas e classificação de leads.

## O que a Qualificação de CNPJ faz

Com o modo de Qualificação de CNPJ, a SilvIA pode:

- Receber CNPJs individuais por meio do chat ou em lote, por uma planilha padronizada do Excel;
- Analisar CNPJs individuais e retornar uma classificação por mensagem, além de gerar um arquivo Word com os resultados, pronto para download;
- Analisar CNPJs em lote e retornar uma planilha do Excel com todos os resultados consolidados, incluindo os dados da empresa e a análise da IA;
- Registrar o histórico das consultas para fins de auditoria pela gerência.

## Como a Qualificação de CNPJ funciona

O fluxo do modo segue os seguintes passos:

1. O usuário ativa o modo e envia sua solicitação;  
2. A SilvIA extrai o CNPJ da mensagem;  
3. A SilvIA realiza buscas pelas informações do CNPJ em diversas fontes públicas;  
4. A inteligência artificial gera uma análise geral com base nos dados encontrados;  
5. A SilvIA classifica o CNPJ quanto à elegibilidade para oferta dos serviços da ViaInvest e indica os produtos mais relevantes para o perfil da empresa;  
6. A resposta é retornada ao usuário via chat, junto com um arquivo Word ou uma planilha Excel.

## Como utilizar a funcionalidade

Para utilizar a ferramenta corretamente, o usuário deve:

1. Ativar o modo de Qualificação de CNPJ;  
2. Enviar um CNPJ por mensagem de texto (com ou sem informações adicionais) **ou** enviar um arquivo Excel padronizado com a coluna CNPJ preenchida (respeitando o limite diário de 20 consultas);  
3. Aguardar o processamento e a resposta da SilvIA;  
4. Caso deseje, ativar o modo de Pesquisa na Web para buscar informações adicionais ou solicitar esclarecimentos sobre a análise gerada no modo padrão.

> **Atenção:**  
> Com o modo Qualificação de CNPJ ativado, a SilvIA não responde perguntas genéricas.  
> O sistema sempre tenta extrair um CNPJ da mensagem. Caso o texto enviado não seja uma solicitação de análise, ocorrerá erro na execução.

### Informações importantes sobre as consultas

- Para análise por texto, são aceitas apenas consultas individuais. Envie somente um CNPJ por mensagem;  
- Para análise em lote, envie um arquivo Excel seguindo o modelo padrão, com apenas a coluna CNPJ preenchida. A SilvIA completará automaticamente as demais colunas com os dados e a análise;  
- Com o modo Qualificação ativado, mensagens sem CNPJ não serão processadas corretamente. Para tirar dúvidas ou comentar a análise, desative o modo.

## Exemplo de uso

### Consulta individual

O usuário ativa o modo de Qualificação de CNPJ e envia:

> **"Silvia, por favor, analise este CNPJ: 46.036.629/0001-92."**

A resposta da SilvIA será algo como:
A SilvIA poderia responder algo como:

> **1. DADOS IDENTIFICADOS DO CNPJ**
>
> **Razão Social:** COMERCIAL VAREJO LTDA

> **CNPJ:** 46.036.629/0001-92

> **Situação Cadastral:** ATIVA

> **Data de Abertura:** 14/04/2022

> **Tempo de Mercado:** 3,7 anos

> **Porte:** Micro Empresa

> **Faturamento Presumido:** Até R\$ 81.000/ano

> **Capital Social:** R\$ 5.000

> **Natureza Jurídica:** Sociedade Empresária Limitada

> **Atividade Principal (CNAE):** 4755502 - Comércio varejista de
> artigos de armarinho

> **Atividades Secundárias:**
> - Comércio atacadista de artigos de armarinho
> - Comércio atacadista de artigos do vestuário e acessórios
> - Comércio atacadista de artigos de escritório e papelaria
> - Comércio atacadista de equipamentos elétricos de uso pessoal e
> doméstico
> - Comércio atacadista de aparelhos eletrônicos de uso pessoal e
> doméstico
> - Comércio atacadista de produtos de higiene, limpeza e conservação
> domiciliar
> - Comércio atacadista de outros equipamentos e artigos de uso pessoal
> e doméstico
> - Lojas de variedades
> - Comércio varejista especializado de equipamentos de telefonia e
> comunicação
> - Comércio varejista especializado de eletrodomésticos e equipamentos
> de áudio e vídeo
> - Comércio varejista de artigos de cama, mesa e banho
> - Comércio varejista especializado de peças e acessórios para
> aparelhos eletroeletrônicos
> - Comércio varejista de outros artigos de uso pessoal e doméstico
> - Comércio varejista de artigos de papelaria
> - Comércio varejista de artigos esportivos
> - Comércio varejista de artigos do vestuário e acessórios
> - Comércio varejista de animais vivos e de artigos e alimentos para
> animais de estimação
> - Comércio varejista de outros produtos não especificados
> anteriormente

> **Endereço:** MAZZEI, 786, SALA 3, VILA MAZZEI

> **Cidade / Estado:** São Paulo / SP

> **Telefone:** (11) 91102547

> **E-mail:** Não informado

> **Website:** https://www.bomvarejista.com/

> **LinkedIn:**
> https://br.linkedin.com/company/varejo-&-cia-gest-o-comercial-e-marketing

> **Sócios:** FILIPE CARDOSO PESSOA

> **Última Atualização:** 21/01/2026
>
> ------------------------------------------------------------------------
>
> **2. ANÁLISE GERAL E INSIGHTS DE NEGÓCIO**
>
> **Modelo de Negócio:**
> B2C. Justificativa: O CNAE principal e as atividades secundárias são
> predominantemente de comércio varejista, com foco direto no consumidor
> final.
>
> **Perfil Operacional:**
> Venda de artigos de armarinho e produtos diversos para o consumidor
> final, com atuação local em São Paulo.
>
> **Parecer do Setor:**
> Setor Preferencial - Alta liquidez de recebíveis.
>
> **Potencial para Serviços Financeiros (VIAINVEST):**
> Conforme matriz de decisão, o produto core é Adquirência (Maquininhas)
> e Antecipação de Cartão de Crédito, devido ao perfil varejista.
>
> ------------------------------------------------------------------------
>
> **3. COMPARAÇÃO COM ICP**
>
>   ------------------------------------------------------------------------
>   Critério                      Situação da Empresa          Alinhamento
>   ----------------------------- ---------------------------- -------------
>   Tempo de Abertura             3,7 anos                     NÃO ATENDE
>
>   Situação Cadastral            Ativa                        ATENDE
>
>   Região Atendida               São Paulo / SP               ATENDE
>
>   Setor / CNAE                  Varejo (Armarinho)           OBSERVAÇÃO
>
>   Faturamento Presumido (ano)   Até R\$ 81.000/ano           NÃO ATENDE
>   ------------------------------------------------------------------------
>
> ------------------------------------------------------------------------
>
> **4. ALINHAMENTO FINAL COM A VIAINVEST**
>
> **Produtos Recomendados:**\
> Adquirência (Maquininhas) e Antecipação de Cartão de Crédito ---
> Justificativa: Atividade varejista com recebíveis pulverizados, porém
> restrito a meios de pagamento.
>
> **Conclusão do Analista:**\
> Renato, a empresa não atende aos critérios mínimos de tempo de mercado
> e faturamento do ICP VIAINVEST. Não há avanço no fluxo comercial.
>
> **Classificação Final:**\
> ( ) Lead Qualificado\
> ( ) Lead em Observação\
> (X) Lead Não Qualificado
>
> "Se quiser simular outros cenários ou analisar outro CNPJ, é só me
> mandar."


## Tabela de auditoria Master

Para garantir visibilidade, controle e segurança, o modo de Qualificação de CNPJ gera uma planilha de auditoria no Excel.  
Nela são registrados todos os acessos, incluindo: data, hora, solicitante e resultados das consultas.  

Essa planilha pode ser baixada e visualizada exclusivamente pela gerência, com todas as informações organizadas e completas.

## Considerações finais

O modo de Qualificação de CNPJ representa um grande avanço no desenvolvimento da SilvIA.  
Foi a primeira funcionalidade criada a partir de uma demanda direta dos usuários, com impacto real no dia a dia do time comercial.

Caso encontre erros, inconsistências ou oportunidades de melhoria, entre em contato com o time de TI.`
      },
    };
  },
  computed: {
    renderedGuide() {
      return DOMPurify.sanitize(marked.parse(this.guides[this.currentTab]));
    },
    hasWikiPermission() {
      const permissions =
        this.$store.state.user?.data?.profile?.permissions || [];
      return permissions.includes(132);
    },
  },
  watch: {
    currentTab(newTab) {
      this.$router.replace({ query: { ...this.$route.query, tab: newTab } });
    },
  },
  methods: {
    goToTab(tab) {
      this.currentTab = tab;
    },
  },
};
</script>

import { marked } from "marked";
import DOMPurify from "dompurify";

export default {
  name: "UserGuide",
  data() {
    const defaultTab = this.$route.query.tab === "wiki" ? "wiki" : "padrão";
    return {
      currentTab: defaultTab,
      guides: {
        padrão: `# Guia de Uso – SilvIA (Modo Padrão)

SilvIA é a assistente virtual da ViaInvest, projetada para **responder perguntas**, **esclarecer dúvidas** e **auxiliar no dia a dia** dos colaboradores.  
No seu modo padrão, ela atua como uma ferramenta de consulta inteligente, utilizando tanto o conteúdo do nosso **Wiki interno**, quanto seu próprio conhecimento técnico e institucional para oferecer respostas úteis e relevantes.

Este guia foi criado para explicar **como funciona essa busca de informações**, e como você pode tirar o máximo proveito da SilvIA em seu uso diário.

---

## O que a SilvIA faz no modo padrão?

No modo padrão, a SilvIA é capaz de:

- Responder perguntas feitas pelos usuários;
- Buscar informações diretamente nas páginas do Wiki interno;
- Complementar as respostas com seu conhecimento integrado, quando necessário;
- **Gerar arquivos novos com base nas solicitações do usuário (TXT, DOCX, PDF, XLSX, entre outros);**
- **Interpretar, ler e analisar arquivos enviados pelos usuários (documentos de texto, planilhas, PDFs, entre outros).**

Ela é uma ferramenta de apoio que pode esclarecer desde **regras internas**, **políticas da empresa**, até **procedimentos técnicos** — tudo com agilidade e precisão.

---

## 1. Como funciona a busca no Wiki

Sempre que você envia uma pergunta, a SilvIA realiza uma busca no Wiki para encontrar a resposta mais adequada. Esse processo funciona assim:

1. Ela analisa os termos principais da sua pergunta;
2. Realiza uma busca com base:
   - No título das páginas;
   - E nas palavras-chave presentes no início do conteúdo das páginas;
3. Caso encontre uma correspondência relevante, responde com base nela;
4. Se não encontrar nada compatível, utiliza seu conhecimento próprio para tentar responder da melhor forma possível.

---

## 2. Seja específico ao perguntar

A qualidade da resposta da IA depende diretamente da clareza e objetividade da sua pergunta.

- Sempre que possível, informe o nome exato da página que deseja consultar;
- Ou utilize palavras-chave que provavelmente estão no título ou no começo da página.

**Exemplo de pergunta genérica (resultado impreciso):**  
> "Como funciona o processo?"

**Exemplo realista de consulta ideal:**  
> "Quem estará na escala de plantão da SoulPay para março de 2025?"

Fazemos essa consulta citando o nome da página, que é **ESCALA - PLANTÃO SOULPAY**. Não precisamos escrever o nome exatamente, mas palavras importantes dele
para que a IA consiga interpretar quais páginas são mais relevantes para sua busca.

---

## 3. Como formular boas perguntas

Para obter respostas mais completas e úteis:

- Use termos específicos e objetivos;
- Informe à IA qual informação você quer, e onde ela pode buscar (quando souber);
- Evite perguntas vagas ou amplas demais;
- Se estiver procurando uma resposta baseada no Wiki, dê preferência por usar os nomes das páginas ou palavras do título.

**Você também pode perguntar assuntos diversos**, como por exemplo:
- "Qual é a fórmula para calcular juros compostos?"
- "Quais os melhores padrões de formatação para o tipo de contrato X?"
- "Me explique a diferença entre um fundo de investimento X e o fundo Y."

A SilvIA pode responder a **perguntas técnicas, operacionais, administrativas ou gerais sobre o dia a dia do trabalho**.

---

## 4. Quando a resposta não vem do Wiki

Se a SilvIA não encontrar uma página adequada para responder sua pergunta, ela irá recorrer ao seu conhecimento interno para formular uma resposta baseada em boas práticas, contexto institucional e informações gerais.

- Nesse caso, ela pode informar que não encontrou uma fonte específica, e ainda assim tentará ajudar com base no que sabe;
- Se for um tema sensível, específico ou regulado por política interna, **é sempre indicado confirmar a informação com um responsável da área ou consultando diretamente o Wiki**.

---

## 5. Uso de arquivos no modo padrão

Além de responder perguntas e buscar no Wiki, a SilvIA possui funcionalidades de **criação e análise de arquivos**:

### Criação de arquivos
- A SilvIA pode **gerar arquivos novos** em diferentes formatos (TXT, DOCX, PDF, XLSX etc.) com base nas suas solicitações;  
- Esses arquivos podem conter relatórios, resumos, planilhas ou qualquer informação solicitada.

### Interpretação e leitura de arquivos enviados
- Você pode enviar arquivos para que a SilvIA **abra, leia e analise** o conteúdo;  
- É possível pedir para ela responder perguntas diretamente sobre esses arquivos;  
- Arquivos suportados incluem: **documentos de texto, planilhas, PDFs e outros formatos comuns**.

 **Exemplo de uso prático:**  
- "SilvIA, analise essa planilha e me diga qual foi o total de vendas em abril."  
- "SilvIA, gere um PDF com as regras de conduta e boas práticas."  

---

## 6. Limites de conhecimento da IA

Por limitações da própria tecnologia, a SilvIA possui um **corte de conhecimento geral em 31 de maio de 2024**.

Isso significa que:

- Ela não tem acesso a fatos, dados públicos ou atualizações que tenham ocorrido depois dessa data (por exemplo, taxas atuais, mudanças recentes de leis e regras, etc), a menos que estejam no Wiki;
- **Exemplo de consulta à qual a SilvIA não teria os dados corretos:**
>"SilvIA, como está o Ibovespa em relação ao mês anterior?" (São dados novos, de fora de seu corte de conhecimento)
- **Exemplo de consulta à qual a SilvIa consegue responder corretamente:**
>"SilvIA, já foi definida a escala de plantão da Soulpay para julho de 2025?" (Dados documentados no Wiki ou dados gerais que não precisam ser atualizados no momento atual)

---

## 7. Recomendações para um uso eficiente

- **7.1** – Saiba o que você está procurando antes de perguntar. Isso ajuda a formular uma boa solicitação;
- **7.2** – Inclua nomes de páginas, termos específicos e contexto no seu pedido;
- **7.3** – **Evite perguntas vagas ou muito genéricas**. A IA pode tentar responder, mas o resultado pode não ser o esperado;
- **7.4** – Se a resposta parecer incompleta ou imprecisa, refaça a pergunta com mais detalhes ou peça esclarecimentos;
- **7.5** – A SilvIA é uma inteligência artificial e pode errar. **Revisar e confirmar informações sensíveis é sempre uma boa prática**.
- **7.6** - Os arquivos gerados pela SilvIA, assim como as páginas geradas do wiki vão ser criados exatamente da forma que você pedir. Logo, quanto menos informações ela tiver sobre o que você pedir, menos conteúdo terá o arquivo gerado. Sempre faça solicitações ricas em informações e contexto para receber o arquivo completo que deseja.
---

## 8. Avisos importantes sobre o uso da SilvIA

- **8.1** – Todas as interações com a IA são **monitoradas e registradas**, visando segurança, controle de qualidade e melhoria da ferramenta;  
- **8.2** – Se você encontrar erros, problemas ou respostas inesperadas, **entre em contato com os desenvolvedores da SilvIA**;  
- **8.3** – Sugestões, ideias e feedbacks são bem-vindos, e ajudam a tornar a IA cada vez mais útil;  
- **8.4** – O uso inadequado ou intencionalmente incorreto da IA pode acarretar em **advertência ou perda de acesso** à ferramenta.

---

## Considerações finais

SilvIA está aqui para facilitar o seu trabalho, esclarecer dúvidas com agilidade e aumentar sua produtividade.  
Quanto mais clara for sua pergunta, mais precisa será a resposta. Sempre que possível, baseie sua solicitação em páginas do Wiki, utilizando títulos e palavras-chave que ajudem a IA a encontrar o conteúdo certo.  
Agora, com a possibilidade de **gerar novos arquivos** e **interpretar arquivos enviados**, você tem ainda mais recursos para tornar seu dia a dia mais eficiente.
`,
        wiki: ` 


**SilvIA** é a assistente virtual da ViaInvest, e no modo Wiki, ela assume o papel de aliada na criação e edição de páginas no nosso Wiki corporativo.  
Este guia foi elaborado para ajudar você a utilizar esse modo de forma eficiente, mantendo a qualidade e a organização do nosso conteúdo interno.

---

## O que é o Modo Wiki da SilvIA?

Com o modo Wiki ativado, a SilvIA é capaz de:

- **Editar** páginas existentes, conforme as instruções recebidas;
- **Criar** novas páginas, com base no que você solicitar.

O uso dessa funcionalidade exige **atenção e cuidado**, já que a IA executará as ações exatamente como forem solicitadas.

---

## 1. A SilvIA executa exatamente o que for pedido, do jeito que for pedido

A SilvIA não interpreta intenções vagas. Ela realiza fielmente o que você escrever.  
Por isso, preste muita atenção nas instruções que for enviar, e certifique-se de que estão **bem detalhadas e específicas**.

**Exemplo de instrução genérica (pouco eficaz):**  
> "Crie uma página para falar sobre os melhores fundos de investimento."

Neste caso, a SilvIA:
- Vai escolher sozinha a categoria onde a página estará;
- Vai definir sozinha o conteúdo (baseando-se no conhecimento que possui);
- Pode criar algo diferente do que você esperava.

Sempre valorize mensagens detalhadas, explícitas e compatíveis com o que realmente deseja.  
Esse cuidado é essencial para mantermos a **organização e a qualidade do nosso Wiki**.

---

## 2. Informe onde a página deve ser criada

Embora não seja um campo obrigatório, é importante escolher a categoria onde estará a página a ser criada. Sem ela, as páginas soltas podem se perder pelo Wiki, e se tornar difíceis de encontrar.

- Sempre que possível, informe a categoria onde a nova página deve ser inserida.

**Exemplo de instrução completa:**  
>- Usuário seleciona uma categoria desejada no menu da SilvIA, como Tecnologia da Informação;
>- Usuário digita o nome da página no campo designado (obrigatório), como Documentação do Projeto SilvIA;
>- Usuário digita seu prompt: "SilvIA, crie a página Documentação do Projeto SilvIA, citando nela os benefícios do uso da SilvIA para produtividade dentro da ViaInvest, citando os pontos X, Y e Z do processo de desenvolvimento, e as seguintes instruções para uso da ferramenta: (...)";
>- Usuário seleciona a opção de criação e envia.

---

## 3. Quanto mais detalhado for o pedido, melhor será o resultado

Essa dica vale **para qualquer uso de inteligência artificial** – e especialmente para o modo Wiki da SilvIA.

- Sempre que for fazer uma solicitação, pense bem nas informações e contexto que está passando.
- Quanto mais ela souber sobre o que você quer, melhor será o resultado entregue.

**Exemplo de prompt ruim (ineficiente):**  
> “Edite a conciliação de arquivos e melhore.”

**Exemplo de prompt bom:**  
> “Edite a página *Teste da SilvIA*. Retire o conteúdo genérico de testes e coloque um ranking com as 10 melhores práticas para criação de prompts para IA. Certifique-se de que o conteúdo esteja bem formatado.”

O segredo está em escrever uma solicitação clara e bem estruturada, que economiza tempo e evita retrabalho.

---

## 4. Dicas gerais para edição de páginas

- **4.1** – Quanto mais explícito e bem explicado, melhor;  
- **4.2** – Diga o que quer alterar e o que deseja manter. Isso evita modificações não intencionais.

**Exemplo:**  
> “SilvIA, edite a página *Teste da SilvIA*. Em vez de 10 melhores práticas, agora deixe 15. Não altere as que já estavam na página, apenas acrescente mais 5. Não altere mais nada.”

---

## 5. Dicas gerais para criação de páginas

- **5.1** – Clareza sempre é essencial;  
- **5.2** – Informe **onde** a página deve estar (categoria já existente, com nome exato, ou nova categoria);  
- **5.3** – Explique claramente o que você espera do conteúdo da nova página.

**Exemplo de prompt eficiente:**  
> “Crie uma página chamada *Convenções de Escrita de E-mail*. A página deve explicar os requisitos de um e-mail profissional na ViaInvest: ortografia correta, assinatura com nome e setor, assunto coerente, e ao final do e-mail incluir ‘A Via é o caminho’. Escreva isso de forma profissional, detalhada e em formato de passo a passo.”

---

## 6. Recomendações gerais de uso do modo Wiki

- **6.1** – Sempre revise suas instruções antes de enviá-las;  
- **6.2** – A IA facilita, mas é importante explicar bem o que é desejado, para conseguir instruí-la corretamente;  
- **6.3** – A verificação do conteúdo gerado é indispensável.  
  A IA pode errar – e é responsabilidade do usuário **revisar tudo com atenção**.  
  Após cada criação ou edição, acesse a página modificada, leia o conteúdo, e ajuste o que for necessário.

---

## 7. Informações e avisos importantes sobre o uso da SilvIA

- **7.1** – Todas as mensagens (do usuário e da IA) são **armazenadas e monitoradas**, para fins de segurança, controle de qualidade e melhoria contínua da ferramenta;  
- **7.2** – Caso você perceba erros, comportamentos inesperados ou problemas na interface, avise os desenvolvedores da SilvIA;  
- **7.3** – **Sugestões, ideias e melhorias são sempre bem-vindas**, e ajudam a tornar a SilvIA uma ferramenta cada vez mais útil para todos;  
- **7.4** – O uso incorreto ou intencionalmente inadequado da IA pode resultar em advertência e até na suspensão de acesso.

---

## Considerações finais

A SilvIA foi desenvolvida para **aumentar a produtividade** e **facilitar a organização do conhecimento dentro da empresa**.  
Seu uso consciente, bem orientado e estruturado é fundamental para garantir resultados positivos e manter a excelência do nosso Wiki.`,
        projetos: `
# Guia de Uso – SilvIA (Modo Projetos)

A funcionalidade **Projetos** da SilvIA foi criada para organizar conversas e adaptar o comportamento da assistente para contextos específicos — de forma parecida com os “projetos” do ChatGPT.

Use projetos quando precisar de foco, consistência e regras claras que se apliquem a várias conversas relacionadas (como uma pasta com comportamento próprio).

---

##  O que os Projetos fazem

Com Projetos você pode:

- **Organizar conversas como pastas:** agrupe chats relacionados para fácil acesso e histórico.  
- **Criar ambientes/contextos especializados:** cada projeto pode ter um “tom”, regras e prioridades diferentes.  
- **Definir instruções personalizadas (mensagem de sistema):** faça a SilvIA agir como especialista/assistente com regras fixas.  
- **Enviar arquivos de projeto:** planilhas, documentos, PDFs ou bases exportadas usados como fonte confiável.  
- **Compartilhar projetos com outros usuários:** colaboração em cima de um mesmo contexto e arquivos, com permissões específicas.  

---

##  Como os Projetos funcionam

Um **Projeto = pasta + instrução de sistema + arquivos opcionais + permissões de compartilhamento**.

### Prioridade das instruções
- As instruções do projeto são enviadas como mensagem de sistema e têm prioridade sobre o comportamento padrão.  
- Instruções passadas dentro de uma conversa podem complementar, mas não devem contrariar regras críticas definidas no projeto.  
- Arquivos servem como fonte de conhecimento adicional, mas não substituem instruções.  

### Herança e escopo
- Toda conversa dentro do projeto recebe automaticamente a instrução do projeto.  
- Arquivos ficam disponíveis como base em todas as conversas do projeto.  
- Conversas fora do projeto seguem o modo padrão da SilvIA e não têm acesso a esses arquivos.  
- Em projetos compartilhados, cada usuário tem acesso apenas ao **seu próprio histórico de conversas**, mantendo a privacidade.  

---

##  Permissões nos projetos

- **Somente leitura** → Uso de arquivos e instrução personalizada em conversas próprias;  
- **Editor** → Uso de arquivos e instrução personalizada em conversas próprias, edição de instrução personalizada e arquivos de projeto;
- **Administrador** → Uso de arquivos e instrução personalizada em conversas próprias, edição de instrução personalizada e arquivos de projeto e gerenciamento de usuários;
- **Proprietário** → Quem criou o projeto; Pode realizar todas as ações anteriores, e renomear ou deletar o projeto.  

---

##  Exemplo prático: RH em um processo seletivo

Um recrutador cria o projeto **“Processo Seletivo – Analista de Desenvolvimento Jr”** e define na instrução personalizada:  

> “Você é um sistema de triagem avançado que analisa currículos e avalia a correspondência do candidato com a vaga descrita, de maneira detalhada.”  

Ele adiciona dois arquivos:  
- **Descrição da vaga.docx** (requisitos e responsabilidades).  
- **Lista de candidatos.xlsx** (informações gerais dos inscritos).  

O projeto é então **compartilhado com a equipe**:  
- Editores podem atualizar arquivos e instruções.  
- Administradores podem gerenciar usuários.  
- Cada recrutador cria suas próprias conversas, mas só enxerga o próprio histórico.  

**Exemplo de uso em conversa:** um recrutador envia o PDF de um currículo e pede para a SilvIA avaliar. A IA cruza os dados do candidato com a descrição da vaga, destaca pontos fortes/fracos e gera uma **pontuação de aderência**.  

Dessa forma, a equipe inteira trabalha com o mesmo contexto e mantém consistência, mas sem perder a individualidade do histórico de cada membro.  

---

##  Criar e gerenciar projetos — passos práticos

### Criar projeto
- Nomeie de forma clara e curta (ex.: *Finanças Pessoais*, *Suporte Técnico*, *Onboarding RH*).  
- Escreva a instrução personalizada (mensagem de sistema).  
- (Opcional) Faça upload de arquivos de projeto.  

### Editar projeto
- Alterar nome;  
- Atualizar instrução;  
- Adicionar ou remover arquivos (arquivos deletados não poderão mais ser usados).  

### Adicionar/Remover conversas
- Mova conversas existentes para o projeto ou crie novas já dentro dele.  

### Excluir projeto
- Ao excluir, todas as conversas dentro dele também serão deletadas.    

---

##  Boas práticas para arquivos de projeto

- Prefira formatos abertos (CSV, XLSX, TXT, PDF).  
- Mantenha arquivos atualizados para não comprometer a análise.  
- Documente os arquivos e seu uso (ex.: *“Descrição da vaga”*, *“Lista de candidatos”*).  
- Evite informações sensíveis sem regras claras de segurança.  

---

 **Resumo:** Os Projetos da SilvIA organizam, padronizam e permitem colaboração estruturada. Eles fornecem consistência para equipes sem abrir mão da privacidade de cada colaborador.

`,
      },
    };
  },

  computed: {
    renderedGuide() {
      return DOMPurify.sanitize(marked.parse(this.guides[this.currentTab]));
    },
    hasWikiPermission() {
      const permissions =
        this.$store.state.user?.data?.profile?.permissions || [];
      return permissions.includes(132);
    },
  },
  methods: {
    goToTab(tab) {
      this.currentTab = tab;
    },
  },
};
</script>

<style scoped>
.user-guide-page {
  max-width: 70%;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.user-guide-page > h1 {
  text-align: center;
  margin-bottom: 2rem;
  color: #e87722;
  margin-top: 1rem;
  font-weight: bold;
}

.content {
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-size: 1.1rem;
  color: #333;
  text-align: justify;
}

.button-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem;
}

.tab-buttons {
  display: flex;
  gap: 0.5rem;
}

.tab-button {
  padding: 0.5rem 0.8rem;
  background: white;
  color: var(--primary);
  border: 1px solid var(--primary);
  cursor: pointer;
  border-radius: 5px;
  font-weight: 500;
}

.tab-button.active {
  background: var(--primary);
  color: white;
}

.back-button {
  margin-top: 2rem;
  margin-left: 1rem;
  padding: 0.5rem 0.6rem;
  background: white;
  color: var(--primary);
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

</style>
